import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, graphConfig } from '../config/authConfig';

const AuthContext = createContext(null);

export const useAzureAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAzureAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, accounts]);

  const fetchUserProfile = async () => {
    try {
      const account = accounts[0];

      // Try to get profile from Microsoft Graph API
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const graphResponse = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });

      if (graphResponse.ok) {
        const profileData = await graphResponse.json();
        setUserProfile({
          id: account.localAccountId,
          email: account.username,
          name: account.name || profileData.displayName,
          username: account.username,
          displayName: profileData.displayName,
          jobTitle: profileData.jobTitle,
          officeLocation: profileData.officeLocation,
        });
      } else {
        // Fallback to account info if Graph API fails
        setUserProfile({
          id: account.localAccountId,
          email: account.username,
          name: account.name,
          username: account.username,
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);

      // Fallback to basic account info
      const account = accounts[0];
      setUserProfile({
        id: account.localAccountId,
        email: account.username,
        name: account.name,
        username: account.username,
      });
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const value = {
    isAuthenticated,
    user: userProfile,
    currentUser: userProfile, // For backwards compatibility with existing code
    loading: loading || inProgress === InteractionStatus.Login || inProgress === InteractionStatus.Logout,
    login,
    logout,
    instance,
    accounts,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
